import { useIsNewUser } from './useIsNewUser';
import { useQuery } from '@tanstack/react-query';
import { recipeService } from '@/services/recipe';
import { RECIPES } from '@/constants/queryKeys';
import { useUser } from '@/contexts/UserContext';

export interface NavigationState {
  canAccessMealPlanner: boolean;
  canAccessShoppingList: boolean;
  canAccessWellness: boolean;
  shouldShowInMoreMenu: (item: string) => boolean;
  isNewUser: boolean;
  isLoading: boolean;
}

export const useNavigationState = (): NavigationState => {
  const { isAuthenticated, user } = useUser();
  const { isNewUser, totalMealPlansCount, isLoading: isNewUserLoading } = useIsNewUser();

  // Get accessible recipes count (same logic as OnboardingFlow)
  const { data: allRecipes = [], isLoading: isLoadingRecipes } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
    enabled: isAuthenticated && !!user,
  });

  const totalAccessibleRecipes = allRecipes.length;

  // Combined loading state
  const isLoading = isLoadingRecipes || isNewUserLoading;

  // Same logic as OnboardingSteps - disabled during loading
  const canAccessMealPlanner = !isLoading && totalAccessibleRecipes > 0;
  const canAccessShoppingList = !isLoading && totalMealPlansCount >= 1;
  const canAccessWellness = !isLoading && !isNewUser;

  // Items to hide in More menu for new users
  const newUserHiddenItems = ['wellness', 'family-activity', 'ai-suggestions', 'monthly-view'];

  const shouldShowInMoreMenu = (item: string) => {
    if (!isNewUser) return true;
    return !newUserHiddenItems.includes(item);
  };

  return {
    canAccessMealPlanner,
    canAccessShoppingList,
    canAccessWellness,
    shouldShowInMoreMenu,
    isNewUser,
    isLoading,
  };
};